// Pure logic for the availability panel (Booking V3, option-led services).
//
// The panel asks get_service_availability_bands for the guest's whole choice
// (what + how many) and draws, per tenant-local day, the bands in which every
// entry of that choice would be accepted by create_booking_hold as a NEW hold.
// Everything here is arithmetic over those answers: day paging, start chips,
// day statuses, copy. It decides nothing about availability itself.
//
// No runtime imports: e2e/availability-panel-logic.spec.ts imports this module
// under Node, where config.ts (import.meta.env) cannot load.
//
// Conventions: instants are epoch milliseconds; days are tenant-local
// "YYYY-MM-DD" strings; an OffsetAt gives the tenant's UTC offset in minutes
// at an instant (tenant-time.ts offsetMinAt).

export type Granularity = 15 | 30 | "any";
/** A band is CLOSED at both ends: s is a valid start iff opensAtMs <= s <= lastStartMs. */
export type Band = { opensAtMs: number; lastStartMs: number; noticeTruncated: boolean };
export type DayBands = { day: string; bands: Band[] };
/** One entry of p_selection, exactly as the RPC takes it. */
export type SelectionEntry = { service_unit_id: string; guests: number; minutes: number | null };
/**
 * "together" / "apart": the chosen options cannot start at the same time that
 * day, but one of them can on its own — for the whole party ("together"), or
 * only for its own headcount ("apart"). dayStatus never returns them; the
 * panel lays them over a "full" day (together-logic.ts togetherStatus).
 */
export type DayStatus = "idle" | "checking" | "unknown" | "free" | "full" | "closed" | "not_offered" | "notice" | "together" | "apart";
export type OffsetAt = (ms: number) => number;

const MIN_MS = 60_000;

// ─── Days ────────────────────────────────────────────────────────────────────

const YMD = /^\d{4}-\d{2}-\d{2}$/;
export function isYmd(v: unknown): v is string {
  return typeof v === "string" && YMD.test(v);
}

function ymdParts(ymd: string): [number, number, number] {
  const [y, m, d] = ymd.split("-").map(Number);
  return [y!, m!, d!];
}

export function addDaysYmd(ymd: string, delta: number): string {
  const [y, m, d] = ymdParts(ymd);
  return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().slice(0, 10);
}

/** 0 = Sunday … 6 = Saturday, like service_hours.day_of_week. */
export function weekdayOfYmd(ymd: string): number {
  const [y, m, d] = ymdParts(ymd);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

// Fixed English arrays, not Intl: the labels must not depend on the ICU build.
const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export type DayLabel = {
  /** "Sun 13 Sept" */
  short: string;
  /** "Sunday 13 September" */
  long: string;
  weekdayShort: string;
  /** "13 Sept" */
  dateShort: string;
  relative: "Today" | "Tomorrow" | null;
};

export function dayLabel(ymd: string, todayYmd: string): DayLabel {
  const [, m, d] = ymdParts(ymd);
  const wd = weekdayOfYmd(ymd);
  const relative = ymd === todayYmd ? "Today" : ymd === addDaysYmd(todayYmd, 1) ? "Tomorrow" : null;
  return {
    short: `${WEEKDAYS_SHORT[wd]} ${d} ${MONTHS_SHORT[m - 1]}`,
    long: `${WEEKDAYS_LONG[wd]} ${d} ${MONTHS_LONG[m - 1]}`,
    weekdayShort: WEEKDAYS_SHORT[wd]!,
    dateShort: `${d} ${MONTHS_SHORT[m - 1]}`,
    relative,
  };
}

/** Last bookable day: max_advance_days capped at 365, like the server's least(coalesce(…, 365), 365). */
export function horizonEnd(todayYmd: string, maxAdvanceDays: number | null): string {
  const days =
    maxAdvanceDays != null && Number.isFinite(maxAdvanceDays) && maxAdvanceDays >= 0
      ? Math.min(Math.floor(maxAdvanceDays), 365)
      : 365;
  return addDaysYmd(todayYmd, days);
}

/** Days per page of the day strip: one bands read covers a week (the RPC's maximum). */
export const DAYS_PER_PAGE = 7;

export function pageDays(
  fromYmd: string,
  endYmd: string,
  size = DAYS_PER_PAGE,
): { days: string[]; hasMore: boolean } {
  const days: string[] = [];
  for (let d = fromYmd; d <= endYmd && days.length < size; d = addDaysYmd(d, 1)) days.push(d);
  const last = days[days.length - 1] ?? fromYmd;
  return { days, hasMore: last < endYmd };
}

export function clampJump(ymd: string, minYmd: string, maxYmd: string): string {
  if (!isYmd(ymd)) return minYmd;
  return ymd < minYmd ? minYmd : ymd > maxYmd ? maxYmd : ymd;
}

// ─── Band rows ───────────────────────────────────────────────────────────────

/**
 * The RPC rows grouped per requested day. Null when the answer is not a row
 * array, so the caller reports an error — a malformed answer is never an
 * empty (= "Full") one. Rows with a bad day, unparseable times or
 * last < opens are dropped; every requested day is present.
 */
export function groupBandRows(
  rows: unknown,
  days: readonly string[],
): Record<string, DayBands> | null {
  if (!Array.isArray(rows)) return null;
  const out: Record<string, DayBands> = {};
  for (const d of days) out[d] = { day: d, bands: [] };
  for (const r of rows) {
    if (!r || typeof r !== "object") continue;
    const row = r as Record<string, unknown>;
    const day = typeof row.day === "string" ? row.day.slice(0, 10) : null;
    if (!day || !out[day]) continue;
    const opens = typeof row.opens_at === "string" ? Date.parse(row.opens_at) : Number.NaN;
    const last = typeof row.last_start_at === "string" ? Date.parse(row.last_start_at) : Number.NaN;
    if (!Number.isFinite(opens) || !Number.isFinite(last) || last < opens) continue;
    out[day]!.bands.push({ opensAtMs: opens, lastStartMs: last, noticeTruncated: row.notice_truncated === true });
  }
  for (const d of days) out[d]!.bands.sort((a, b) => a.opensAtMs - b.opensAtMs);
  return out;
}

/**
 * Whether the engine told us which bands the notice rule truncated (the
 * 5-column contract). An older 4-column engine does not; the panel then shows
 * a text line for today's notice instead of a hatch.
 */
export function bandRowsCarryNotice(rows: unknown): boolean {
  if (!Array.isArray(rows)) return false;
  if (rows.length === 0) return true;
  return rows.some((r) => !!r && typeof r === "object" && typeof (r as Record<string, unknown>).notice_truncated === "boolean");
}

// ─── Starts ──────────────────────────────────────────────────────────────────

function onLocalGrid(ms: number, gran: 15 | 30, offsetAt: OffsetAt): boolean {
  const step = gran * MIN_MS;
  const local = ms + offsetAt(ms) * MIN_MS;
  return ((local % step) + step) % step === 0;
}

/**
 * The tenant-local :00/:15/:30/:45 points inside a band (last_start_at
 * inclusive). A DST shift inside the band re-aligns to the local grid.
 */
export function startChips(band: Band, gran: 15 | 30, offsetAt: OffsetAt): number[] {
  const step = gran * MIN_MS;
  const out: number[] = [];
  let off = offsetAt(band.opensAtMs) * MIN_MS;
  let g = Math.ceil((band.opensAtMs + off) / step) * step - off;
  for (let guard = 0; g <= band.lastStartMs && guard < 5000; guard++) {
    const offG = offsetAt(g) * MIN_MS;
    if (offG !== off) {
      off = offG;
      const aligned = Math.ceil((g + off) / step) * step - off;
      if (aligned !== g) {
        g = aligned;
        continue;
      }
    }
    out.push(g);
    g += step;
  }
  return out;
}

/**
 * How close to "now" an opening must be to count as the engine's now-floor.
 * The RPC never returns a start before ceil_minute(now()), so on a service
 * with no notice rule today's band opens at that minute with
 * notice_truncated false. The slack covers the panel's minute clock, the
 * 60-second band cache and a little clock skew.
 */
export const NOW_FLOOR_SLACK_MS = 5 * MIN_MS;

/**
 * A band's own first and last start when they fall off the grid (a turnaround
 * or a booking's end can open a band at 10:15 on a 30-minute grid) — always
 * offered, so a day with a band never reads Full. Exceptions (owner decision
 * and its reason): when notice truncated the band's opening, its opening edge
 * is "now + notice", not a real edge, so only the grid inside it is offered;
 * the same holds for an opening that is only the clock's now-floor (pass
 * `nowMs`) — "now + 0" is no more a real edge, and it goes stale in a minute.
 */
export function edgeChips(band: Band, gran: 15 | 30, offsetAt: OffsetAt, nowMs?: number): number[] {
  const out: number[] = [];
  const nowFloor = nowMs != null && band.opensAtMs <= nowMs + NOW_FLOOR_SLACK_MS;
  if (!band.noticeTruncated && !nowFloor && !onLocalGrid(band.opensAtMs, gran, offsetAt)) out.push(band.opensAtMs);
  if (band.lastStartMs !== band.opensAtMs && !onLocalGrid(band.lastStartMs, gran, offsetAt)) out.push(band.lastStartMs);
  return out;
}

/** What the start picker offers for a band: grid ∪ edges, sorted, de-duplicated. */
export function pickerChips(band: Band, gran: 15 | 30, offsetAt: OffsetAt, nowMs?: number): number[] {
  return [...new Set([...startChips(band, gran, offsetAt), ...edgeChips(band, gran, offsetAt, nowMs)])].sort((a, b) => a - b);
}

/** The "any time" input's bounds: whole minutes inside the band, or null. */
export function anyStartBounds(band: Band): { minMs: number; maxMs: number } | null {
  const minMs = Math.ceil(band.opensAtMs / MIN_MS) * MIN_MS;
  const maxMs = Math.floor(band.lastStartMs / MIN_MS) * MIN_MS;
  return minMs <= maxMs ? { minMs, maxMs } : null;
}

export function bandStarts(band: Band, gran: Granularity, offsetAt: OffsetAt, nowMs?: number): number[] {
  if (gran === "any") {
    const b = anyStartBounds(band);
    return b ? [b.minMs] : [];
  }
  return pickerChips(band, gran, offsetAt, nowMs);
}

export function hasPickableStart(
  bands: readonly Band[],
  gran: Granularity,
  offsetAt: OffsetAt,
  nowMs?: number,
): boolean {
  return bands.some((b) => bandStarts(b, gran, offsetAt, nowMs).length > 0);
}

export function containsStart(bands: readonly Band[], startMs: number): boolean {
  return bands.some((b) => b.opensAtMs <= startMs && startMs <= b.lastStartMs);
}

/**
 * A day whose every band is a single start — the option runs at fixed times
 * (availability_rules kind start_times) — is offered as those starts in one
 * row, with no "start window" step first. null when any band spans more than
 * one start. Starts the clock has passed are dropped.
 */
export function fixedStarts(bands: readonly Band[], nowMs?: number): number[] | null {
  if (bands.length === 0 || bands.some((b) => b.lastStartMs !== b.opensAtMs)) return null;
  const floor = nowMs == null ? Number.NEGATIVE_INFINITY : Math.ceil(nowMs / MIN_MS) * MIN_MS;
  return [...new Set(bands.map((b) => b.opensAtMs))].filter((ms) => ms >= floor).sort((a, b) => a - b);
}

// ─── Day status ──────────────────────────────────────────────────────────────

export function dayStatus(i: {
  day: string;
  todayYmd: string;
  request: "idle" | "loading" | "error" | "ready";
  bands: readonly Band[] | undefined;
  gran: Granularity;
  offsetAt: OffsetAt;
  closedDates: ReadonlySet<string>;
  hasHours: boolean;
  openWindowCount: number;
  noticeMin: number;
  nowMs: number;
  lastWindowEndMs: number | null;
  longestMin: number;
  /** Unit-specific schedule metadata explains empty bands, never overrides starts. */
  scheduleRequest?: "loading" | "error" | "ready";
  notOffered?: boolean;
}): DayStatus {
  if (i.request === "idle") return "idle";
  if (i.request === "loading") return "checking";
  const knownClosed = i.closedDates.has(i.day) || (i.hasHours && i.openWindowCount === 0);
  // Today, and nothing can start early enough to end by the last close: the
  // notice rule (or the clock) ruled the rest of the day out.
  const tooSoon =
    i.day === i.todayYmd &&
    i.lastWindowEndMs != null &&
    i.nowMs + Math.max(0, i.noticeMin) * MIN_MS > i.lastWindowEndMs - i.longestMin * MIN_MS;
  // Returned starts win over cached schedule metadata. For empty/error reads,
  // distinguish a venue closure from the selected option's non-operating day.
  if (i.request === "ready" && i.bands && hasPickableStart(i.bands, i.gran, i.offsetAt, i.nowMs)) return "free";
  if (knownClosed) return "closed";
  if (i.scheduleRequest === "loading") return "checking";
  if (i.scheduleRequest === "error") return "unknown";
  if (i.notOffered) return "not_offered";
  if (tooSoon) return "notice";
  if (i.request === "error") return "unknown";
  return "full";
}

// ─── Words ───────────────────────────────────────────────────────────────────

export function formatLength(min: number): string {
  if (min % 60 === 0) return `${min / 60} h`;
  if (min < 120) return `${min} min`;
  return `${Math.floor(min / 60)} h ${min % 60} min`;
}

export function lengthAdjective(min: number): string {
  return min % 60 === 0 ? `${min / 60}-hour` : `${min}-minute`;
}

export function noticeText(min: number): string {
  if (min === 60) return "1 hour";
  if (min % 60 === 0) return `${min / 60} hours`;
  if (min < 120) return `${min} minutes`;
  return `${Math.floor(min / 60)} h ${min % 60} min`;
}

export function noticeReason(min: number): string {
  if (min === 60) return "1 hour's notice";
  if (min % 60 === 0) return `${min / 60} hours' notice`;
  if (min < 120) return `${min} minutes' notice`;
  return `${Math.floor(min / 60)} h ${min % 60} min notice`;
}

export function joinOr(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  if (parts.length === 2) return `${parts[0]} or ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, or ${parts[parts.length - 1]}`;
}

export function joinAnd(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/**
 * The true reason a day offers nothing, built from the selection. Never a
 * count, never a slug. `oversize`: a party the venue takes by enquiry only
 * (the enquiry CTA sits beside it, and "another day" would not help).
 */
export function emptyDayCopy(i: {
  dayShort: string;
  status: "full" | "closed" | "not_offered" | "notice" | "oversize";
  shape: "sessions" | "multi";
  noun: { one: string; other: string };
  entries: readonly { name: string; guests: number; lengthLabel: string | null }[];
  sessionMinutes: number | null;
  shorterExists: boolean;
  otherDaysExist: boolean;
  noticeMin: number;
  closureMessage: string | null;
  oversizeMax?: number | null;
  /** Only the selected options blocked by their schedule, not the entire selection. */
  notOfferedNames?: readonly string[];
}): string {
  const n = (g: number) => (g === 1 ? i.noun.one : i.noun.other);
  const tail = i.otherDaysExist ? " Try another day." : "";
  if (i.status === "oversize") {
    return i.oversizeMax != null
      ? `Groups larger than ${i.oversizeMax} ${i.noun.other} book by enquiry — send one and the venue will arrange it with you.`
      : `Groups this size book by enquiry — send one and the venue will arrange it with you.`;
  }
  if (i.status === "closed") return `${i.closureMessage ?? `${i.dayShort} is closed for bookings.`}${tail}`;
  if (i.status === "not_offered") {
    const names = i.notOfferedNames ?? i.entries.map((e) => e.name);
    const subject = names.length ? joinAnd(names) : "This option";
    return `${subject} ${names.length > 1 ? "aren't" : "isn't"} offered on ${i.dayShort}.${tail}`;
  }
  if (i.status === "notice") {
    return i.noticeMin > 0
      ? `Today is too soon — bookings need ${noticeReason(i.noticeMin)}.${tail}`
      : `It's too late to book today.${tail}`;
  }
  const total = i.entries.reduce((s, e) => s + e.guests, 0);
  let head: string;
  if (i.shape === "sessions") {
    const g = i.entries[0]?.guests ?? 1;
    head =
      i.sessionMinutes != null
        ? `Nothing free for ${g} ${n(g)} in a ${lengthAdjective(i.sessionMinutes)} session on ${i.dayShort}.`
        : `Nothing free for ${g} ${n(g)} on ${i.dayShort}.`;
  } else {
    const parts = i.entries.map((e) => `${e.guests} ${n(e.guests)} on ${e.name}${e.lengthLabel ? ` (${e.lengthLabel})` : ""}`);
    head = `Nothing free for ${joinAnd(parts)} on ${i.dayShort}.`;
  }
  const s: string[] = [];
  if (total > 1) s.push(`fewer ${i.noun.other}`);
  if (i.shorterExists) s.push(i.shape === "sessions" ? "a shorter session" : "a shorter length");
  if (i.shape === "multi" && i.entries.length > 1) s.push("fewer options");
  if (i.otherDaysExist) s.push("another day");
  return s.length ? `${head} Try ${joinOr(s)}.` : head;
}

// ─── Placing a visit ─────────────────────────────────────────────────────────

export type HoldFailureLike = { reason: string; [k: string]: unknown };
export type HoldAttempt<L> =
  | { ok: true; line: L; bookingId: string }
  | { ok: false; failure: HoldFailureLike };
export type PlacedHold<E, L> = { entry: E; line: L; bookingId: string };
export type VisitHoldOutcome<E, L> =
  | { kind: "placed"; placed: PlacedHold<E, L>[] }
  | {
      kind: "refused";
      entry: E;
      failure: HoldFailureLike;
      /** Holds this Add placed and then released (confirmed). */
      released: string[];
      /** Holds whose release could not be confirmed — still live, display order. */
      kept: PlacedHold<E, L>[];
    };

/**
 * One visit = every entry at the same start, all or nothing. Holds are placed
 * one at a time IN THE GIVEN ORDER (the p_selection order the bands were
 * computed for — a different order can refuse a visit the band showed free).
 * On the first refusal every hold already placed is released, newest first;
 * a release that cannot be confirmed is returned in `kept`, never hidden.
 */
export async function holdVisitEntries<E, L>(
  entries: readonly E[],
  deps: {
    hold: (entry: E) => Promise<HoldAttempt<L>>;
    release: (bookingId: string) => Promise<"released" | "unknown">;
  },
): Promise<VisitHoldOutcome<E, L>> {
  const placed: PlacedHold<E, L>[] = [];
  for (const entry of entries) {
    let r: HoldAttempt<L>;
    try {
      r = await deps.hold(entry);
    } catch {
      r = { ok: false, failure: { reason: "network_error" } };
    }
    if (!r.ok) {
      const released: string[] = [];
      const kept: PlacedHold<E, L>[] = [];
      for (const p of [...placed].reverse()) {
        let verdict: "released" | "unknown";
        try {
          verdict = await deps.release(p.bookingId);
        } catch {
          verdict = "unknown";
        }
        if (verdict === "released") released.push(p.bookingId);
        else kept.unshift(p);
      }
      return { kind: "refused", entry, failure: r.failure, released, kept };
    }
    placed.push({ entry, line: r.line, bookingId: r.bookingId });
  }
  return { kind: "placed", placed };
}

// ─── The visit already in the cart ───────────────────────────────────────────

/** The fields of a cart line the visit accounting reads (a CartItem has them). */
export type VisitCartLine = {
  serviceId: string;
  unitId: string | null;
  metadata?: Record<string, unknown> | null;
};

/**
 * This service's cart lines held at `startMs`: one visit is one service, one
 * start, one cart. Instants are compared, never strings (the hold answers
 * "+00:00" where the panel sent "Z").
 */
export function visitLinesAt<L extends VisitCartLine>(items: readonly L[], serviceId: string, startMs: number): L[] {
  return items.filter((it) => {
    if (it.serviceId !== serviceId) return false;
    const s = Date.parse(String(it.metadata?.starts_at ?? ""));
    return Number.isFinite(s) && s === startMs;
  });
}

/**
 * An Add at a start where part of the visit is already in the cart — an
 * earlier Add, or a line kept after a release that could not be confirmed,
 * even by a panel that has since closed. The cart is the record, not the
 * panel: hold only the options not yet held at that start (one line per
 * option per start, the rule the date-first cards kept), and stamp the WHOLE
 * visit's guests — set_cart_party_size writes every pending row of the cart,
 * so a stamp of this Add's guests alone would undercount the visit.
 * Combo lines travel as a package: they count as guests, never as the option.
 */
export function planVisitAdd<E extends { unit: { id: string }; guests: number }>(
  entries: readonly E[],
  inVisit: readonly VisitCartLine[],
): { toHold: E[]; alreadyIn: E[]; heldGuests: number; visitGuests: number } {
  const heldUnits = new Set<string>();
  for (const l of inVisit) if (l.unitId && !l.metadata?.combo_id) heldUnits.add(l.unitId);
  const toHold = entries.filter((e) => !heldUnits.has(e.unit.id));
  const alreadyIn = entries.filter((e) => heldUnits.has(e.unit.id));
  const heldGuests = inVisit.reduce((n, l) => {
    const p = l.metadata?.party_size;
    return typeof p === "number" && Number.isFinite(p) && p > 0 ? n + p : n;
  }, 0);
  return { toHold, alreadyIn, heldGuests, visitGuests: heldGuests + toHold.reduce((n, e) => n + e.guests, 0) };
}
